import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarCheck, CalendarClock, MessageCircleReply, X } from "lucide-react";

const fmt = (iso) => new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });

// Pastille sur la ligne SAV : réponse du client au lien SMS
export const SavClientResponseBadge = ({ sav, onSeen }) => {
  const r = sav.client_response;
  if (!r) return null;
  const confirm = r.action === "confirm";
  const markSeen = async () => {
    await api.markSavResponseSeen(sav.id);
    window.dispatchEvent(new Event("sav-responses-changed"));
    onSeen?.();
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`sav-client-response-${sav.id}`}>
          <Badge variant="outline" className={`ml-1 cursor-pointer ${confirm ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"} ${r.seen ? "opacity-60" : ""}`}>
            {confirm ? <CalendarCheck className="h-3 w-3 mr-1" /> : <CalendarClock className="h-3 w-3 mr-1" />}
            {confirm ? "Client : confirmé" : "Client : autre créneau"}
            {!r.seen && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-current" />}
          </Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 text-sm space-y-2">
        <p className="font-medium">{confirm ? "Le client a confirmé" : "Le client demande un autre créneau"}</p>
        <p className="text-xs text-muted-foreground">Le {fmt(r.at)}</p>
        {r.message && <p className="rounded bg-muted p-2 whitespace-pre-wrap" data-testid={`sav-client-response-message-${sav.id}`}>{r.message}</p>}
        {!r.seen && (
          <Button size="sm" variant="outline" onClick={markSeen} data-testid={`sav-client-response-seen-${sav.id}`}>Marquer comme traité</Button>
        )}
      </PopoverContent>
    </Popover>
  );
};

// Bandeau global : réponses clients non traitées
export const SavResponsesBanner = () => {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const load = useCallback(() => {
    api.getUnseenSavResponses().then((r) => setData(r.data)).catch(() => setData(null));
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 120000);
    window.addEventListener("sav-responses-changed", load);
    return () => {
      clearInterval(t);
      window.removeEventListener("sav-responses-changed", load);
    };
  }, [load]);
  if (hidden || !data?.count) return null;
  const reschedule = data.items.filter((i) => i.client_response?.action === "reschedule").length;
  return (
    <div className="mb-4 flex items-center gap-3 rounded-md border border-indigo-200 bg-indigo-50 px-4 py-2.5 text-sm text-indigo-900" data-testid="sav-responses-banner">
      <MessageCircleReply className="h-4 w-4 shrink-0" />
      <span className="flex-1">
        {data.count} réponse(s) client SAV à traiter{reschedule ? ` dont ${reschedule} demande(s) d'un autre créneau` : ""}.
      </span>
      <Link to="/sav" className="font-medium underline" data-testid="sav-responses-banner-link">Voir les SAV</Link>
      <button type="button" onClick={() => setHidden(true)} aria-label="Masquer" data-testid="sav-responses-banner-close">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};
